import { JobId } from '@eigtech/restoration-types'
import { InspectionCorn } from '@eigtech/inspections-types'
import { RoutesContext } from '@eigtech/ui-shared-router'
import { FileRouteTypes } from '../../routeTree.gen'
import { GuidedDocumentTypeEnum } from '@eigtech/documator-types'

export const ROUTER_PATHS = {
  home: {
    routeId: '/_auth/',
    route: '/',
    url: '/',
  },

  claims: {
    routeId: '/_auth/claims/',
    route: '/claims',
    url: '/claims',
  },

  claimDetail: {
    routeId: '/_auth/claims/$claimNumber',
    route: '/claims/$claimNumber',
    url: (claimNumber: string) => `/claims/${claimNumber}`,
  },
  claimDetailInfo: {
    routeId: '/_auth/claims/$claimNumber/_layout/info',
    route: '/claims/$claimNumber/info',
    url: (claimNumber: string) => `/claims/${claimNumber}/info`,
  },
  claimDetailSchedule: {
    routeId: '/_auth/claims/$claimNumber/_layout/schedule',
    route: '/claims/$claimNumber/schedule',
    url: (claimNumber: string) => `/claims/${claimNumber}/schedule`,
  },
  claimDetailEstimates: {
    routeId: '/_auth/claims/$claimNumber/_layout/estimates',
    route: '/claims/$claimNumber/estimates',
    url: (claimNumber: string) => `/claims/${claimNumber}/estimates`,
  },
  claimDetailEstimateDetail: {
    routeId: '/_auth/claims/$claimNumber/_layout/estimates_/$estimateId',
    route: '/claims/$claimNumber/estimates/$estimateId',
    url: ({ claimNumber, estimateId }: { claimNumber: string; estimateId: string }) =>
      `/claims/${claimNumber}/estimates/${estimateId}`,
  },
  claimDetailGenerateDocument: {
    routeId: '/_auth/claims/$claimNumber/_layout/generate-document',
    route: '/claims/$claimNumber/generate-document',
    url: ({ claimNumber }: { claimNumber: string }) => `/claims/${claimNumber}/generate-document`,
  },
  claimDetailGenerateDocumentStatus: {
    routeId: '/_auth/claims/$claimNumber/_layout/generate-document_/status/$templateId',
    route: '/claims/$claimNumber/generate-document/status/$templateId',
    url: ({ claimNumber, templateId }: { claimNumber: string; templateId: string }) =>
      `/claims/${claimNumber}/generate-document/status/${templateId}`,
  },
  claimDetailGenerateDocumentGuided: {
    routeId: '/_auth/claims/$claimNumber/_layout/generate-document_/guided/$guidedDocumentType',
    route: '/claims/$claimNumber/generate-document/guided/$guidedDocumentType',
    url: ({
      claimNumber,
      guidedDocumentType,
    }: {
      claimNumber: string
      guidedDocumentType: GuidedDocumentTypeEnum
    }) => `/claims/${claimNumber}/generate-document/guided/${guidedDocumentType}`,
  },
  claimDetailMap: {
    routeId: '/_auth/claims/$claimNumber/_layout/info_/map',
    route: '/claims/$claimNumber/info/map',
    url: (claimNumber: string) => `/claims/${claimNumber}/info/map`,
  },
  claimDetailTimeline: {
    routeId: '/_auth/claims/$claimNumber/_layout/timeline',
    route: '/claims/$claimNumber/timeline',
    url: (claimNumber: string) => `/claims/${claimNumber}/timeline`,
  },
  claimDetailCallbacks: {
    routeId: '/_auth/claims/$claimNumber/_layout/callbacks',
    route: '/claims/$claimNumber/callbacks',
    url: (claimNumber: string) => `/claims/${claimNumber}/callbacks`,
  },
  claimDetailContacts: {
    routeId: '/_auth/claims/$claimNumber/_layout/contacts',
    route: '/claims/$claimNumber/contacts',
    url: (claimNumber: string) => `/claims/${claimNumber}/contacts`,
  },
  claimDetailDocuments: {
    routeId: '/_auth/claims/$claimNumber/_layout/documents',
    route: '/claims/$claimNumber/documents',
    url: (claimNumber: string) => `/claims/${claimNumber}/documents`,
  },
  claimDetailLossReport: {
    routeId: '/_auth/claims/$claimNumber/_layout/loss-report',
    route: '/claims/$claimNumber/loss-report',
    url: (claimNumber: string) => `/claims/${claimNumber}/loss-report`,
  },
  claimDetailMedia: {
    routeId: '/_auth/claims/$claimNumber/_layout/media',
    route: '/claims/$claimNumber/media',
    url: (claimNumber: string) => `/claims/${claimNumber}/media`,
  },
  claimDetailInspections: {
    routeId: '/_auth/claims/$claimNumber/_layout/inspections',
    route: '/claims/$claimNumber/inspections',
    url: (claimNumber: string) => `/claims/${claimNumber}/inspections`,
  },
  claimDetailInspectionDetail: {
    routeId: '/_auth/claims/$claimNumber/_layout/inspections_/$inspectionId',
    route: '/claims/$claimNumber/inspections/$inspectionId',
    url: ({ claimNumber, inspectionId }: { claimNumber: string; inspectionId: InspectionCorn }) =>
      `/claims/${claimNumber}/inspections/${inspectionId}`,
  },
  claimDetailMeetings: {
    routeId: '/_auth/claims/$claimNumber/_layout/meetings',
    route: '/claims/$claimNumber/meetings',
    url: (claimNumber: string) => `/claims/${claimNumber}/meetings`,
  },
  claimDetailMeeting: {
    routeId: '/_auth/claims/$claimNumber/_layout/meetings_/$meetingStartTime/$meetingId',
    route: '/claims/$claimNumber/meetings/$meetingStartTime/$meetingId',
    url: ({
      claimNumber,
      meetingId,
      meetingStartTime,
    }: {
      claimNumber: string
      meetingStartTime: string
      meetingId: string
    }) => `/claims/${claimNumber}/meetings/${meetingStartTime}/${meetingId}`,
  },
  claimDetailNewMeeting: {
    routeId: '/_auth/claims/$claimNumber/_layout/meetings_/new',
    route: '/claims/$claimNumber/meetings/new',
    url: (claimNumber: string) => `/claims/${claimNumber}/meetings/new`,
  },
  claimDetailNewMeetingFromVideoUpload: {
    routeId: '/_auth/claims/$claimNumber/_layout/meetings_/new-from-upload',
    route: '/claims/$claimNumber/meetings/new-from-upload',
    url: (claimNumber: string) => `/claims/${claimNumber}/meetings/new-from-upload`,
  },
  claimDetailMeetingUpload: {
    routeId: '/_auth/claims/$claimNumber/_layout/meetings_/$meetingStartTime/$meetingId_/upload',
    route: '/claims/$claimNumber/meetings/$meetingStartTime/$meetingId/upload',
    url: ({
      claimNumber,
      meetingId,
      meetingStartTime,
    }: {
      claimNumber: string
      meetingStartTime: string
      meetingId: string
    }) => `/claims/${claimNumber}/meetings/${meetingStartTime}/${meetingId}/upload`,
  },
  claimDetailNotes: {
    routeId: '/_auth/claims/$claimNumber/_layout/notes',
    route: '/claims/$claimNumber/notes',
    url: (claimNumber: string) => `/claims/${claimNumber}/notes`,
  },
  claimDetailPhotoReport: {
    routeId: '/_auth/claims/$claimNumber/_layout/photo-report',
    route: '/claims/$claimNumber/photo-report',
    url: (claimNumber: string) => `/claims/${claimNumber}/photo-report`,
  },
  claimDetailPhotoReportStatus: {
    routeId: '/_auth/claims/$claimNumber/_layout/photo-report_/status',
    route: '/claims/$claimNumber/photo-report/status',
    url: (claimNumber: string) => `/claims/${claimNumber}/photo-report/status`,
  },
  claimDetailUploadDocument: {
    routeId: '/_auth/claims/$claimNumber/_layout/upload',
    route: '/claims/$claimNumber/upload',
    url: (claimNumber: string) => `/claims/${claimNumber}/upload`,
  },

  jobs: {
    routeId: '/_auth/jobs/',
    route: '/jobs',
    url: '/jobs',
  },

  jobDetail: {
    routeId: '/_auth/jobs/$jobId/_layout/',
    route: '/jobs/$jobId',
    url: (jobId: JobId) => `/jobs/${jobId}`,
  },
  jobDetailAppointments: {
    routeId: '/_auth/jobs/$jobId/_layout/appointments',
    route: '/jobs/$jobId/appointments',
    url: (jobId: JobId) => `/jobs/${jobId}/appointments`,
  },
  jobDetailInfo: {
    routeId: '/_auth/jobs/$jobId/_layout/info',
    route: '/jobs/$jobId/info',
    url: (jobId: JobId) => `/jobs/${jobId}/info`,
  },
  jobDetailDocuments: {
    routeId: '/_auth/jobs/$jobId/_layout/documents',
    route: '/jobs/$jobId/documents',
    url: (jobId: JobId) => `/jobs/${jobId}/documents`,
  },
  jobDetailMedia: {
    routeId: '/_auth/jobs/$jobId/_layout/media',
    route: '/jobs/$jobId/media',
    url: (jobId: JobId) => `/jobs/${jobId}/media`,
  },
  jobDetailBilling: {
    routeId: '/_auth/jobs/$jobId/_layout/billing',
    route: '/jobs/$jobId/billing',
    url: (jobId: JobId) => `/jobs/${jobId}/billing`,
  },
  jobDetailBillDetail: {
    routeId: '/_auth/jobs/$jobId/_layout/billing_/bill/$billId',
    route: '/jobs/$jobId/billing/bill/$billId',
    url: ({ jobId, billId }: { jobId: JobId; billId: string }) =>
      `/jobs/${jobId}/billing/bill/${billId}`,
  },
  jobDetailInspections: {
    routeId: '/_auth/jobs/$jobId/_layout/inspections',
    route: '/jobs/$jobId/inspections',
    url: (jobId: JobId) => `/jobs/${jobId}/inspections`,
  },
  jobDetailInspectionDetail: {
    routeId: '/_auth/jobs/$jobId/_layout/inspections_/$inspectionId',
    route: '/jobs/$jobId/inspections/$inspectionId',
    url: ({ jobId, inspectionId }: { jobId: JobId; inspectionId: InspectionCorn }) =>
      `/jobs/${jobId}/inspections/${inspectionId}`,
  },
  jobDetailMeetings: {
    routeId: '/_auth/jobs/$jobId/_layout/meetings',
    route: '/jobs/$jobId/meetings',
    url: (jobId: JobId) => `/jobs/${jobId}/meetings`,
  },
  jobDetailMeeting: {
    routeId: '/_auth/jobs/$jobId/_layout/meetings_/$meetingStartTime/$meetingId',
    route: '/jobs/$jobId/meetings/$meetingStartTime/$meetingId',
    url: ({
      jobId,
      meetingId,
      meetingStartTime,
    }: {
      jobId: JobId
      meetingStartTime: string
      meetingId: string
    }) => `/jobs/${jobId}/meetings/${meetingStartTime}/${meetingId}`,
  },
  jobDetailNewMeeting: {
    routeId: '/_auth/jobs/$jobId/_layout/meetings_/new',
    route: '/jobs/$jobId/meetings/new',
    url: (jobId: JobId) => `/jobs/${jobId}/meetings/new`,
  },
  jobDetailNewMeetingFromVideoUpload: {
    routeId: '/_auth/jobs/$jobId/_layout/meetings_/new-from-upload',
    route: '/jobs/$jobId/meetings/new-from-upload',
    url: (jobId: JobId) => `/jobs/${jobId}/meetings/new-from-upload`,
  },
  jobDetailMeetingUpload: {
    routeId: '/_auth/jobs/$jobId/_layout/meetings_/$meetingStartTime/$meetingId_/upload',
    route: '/jobs/$jobId/meetings/$meetingStartTime/$meetingId/upload',
    url: ({
      jobId,
      meetingId,
      meetingStartTime,
    }: {
      jobId: JobId
      meetingStartTime: string
      meetingId: string
    }) => `/jobs/${jobId}/meetings/${meetingStartTime}/${meetingId}/upload`,
  },
  jobDetailNotes: {
    routeId: '/_auth/jobs/$jobId/_layout/notes',
    route: '/jobs/$jobId/notes',
    url: (jobId: JobId) => `/jobs/${jobId}/notes`,
  },
  jobDetailUploadDocuments: {
    routeId: '/_auth/jobs/$jobId/_layout/upload',
    route: '/jobs/$jobId/upload',
    url: (jobId: JobId) => `/jobs/${jobId}/upload`,
  },
  jobDetailPhotoReport: {
    routeId: '/_auth/jobs/$jobId/_layout/photo-report',
    route: '/jobs/$jobId/photo-report',
    url: (jobId: JobId) => `/jobs/${jobId}/photo-report`,
  },
  jobDetailPhotoReportStatus: {
    routeId: '/_auth/jobs/$jobId/_layout/photo-report_/status',
    route: '/jobs/$jobId/photo-report/status',
    url: (jobId: JobId) => `/jobs/${jobId}/photo-report/status`,
  },
  callbacks: {
    routeId: '/_auth/callbacks/',
    route: '/callbacks',
    url: '/callbacks',
  },
  documentation: {
    routeId: '/_auth/documentation/_layout/',
    route: '/documentation',
    url: '/documentation',
  },
  documentationDetail: {
    routeId: '/_auth/documentation/_layout/$slug',
    route: '/documentation/$slug',
    url: (slug: string) => `/documentation/${slug}`,
  },
  meeting: {
    routeId: '/_auth/meeting',
    route: '/meeting',
    url: '/meeting',
  },
  inbox: {
    routeId: '/_auth/messaging/_layout/',
    route: '/messaging',
    url: '/messaging',
  },
  inboxMessage: {
    routeId: '/_auth/messaging/_layout/$messageId',
    route: '/messaging/$messageId',
    url: ({ messageId }: { messageId: string }) => `/messaging/${messageId}`,
  },
  schedule: {
    routeId: '/_auth/schedule',
    route: '/schedule',
    url: '/schedule',
  },
} satisfies Record<
  string,
  {
    route: FileRouteTypes['to']
    routeId: FileRouteTypes['id']
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    url: string | ((...args: any[]) => string)
  }
>

export const ROUTES_CONTEXT: RoutesContext = {
  home: ROUTER_PATHS.home.url,
  claims: ROUTER_PATHS.claims.url,
  activeCallbacks: ROUTER_PATHS.callbacks.url,
  documentation: ROUTER_PATHS.documentation.url,

  inbox: ROUTER_PATHS.inbox.url,
  inboxMessage: ROUTER_PATHS.inboxMessage.url,

  claimDetail: ROUTER_PATHS.claimDetail.url,
  claimDetailCallbacks: ROUTER_PATHS.claimDetailCallbacks.url,
  claimDetailContacts: ROUTER_PATHS.claimDetailContacts.url,
  claimDetailDocuments: ROUTER_PATHS.claimDetailDocuments.url,
  claimDetailEstimateDetail: ROUTER_PATHS.claimDetailEstimateDetail.url,
  claimDetailEstimates: ROUTER_PATHS.claimDetailEstimates.url,
  claimDetailGenerateDocument: ROUTER_PATHS.claimDetailGenerateDocument.url,
  claimDetailGenerateDocumentStatus: ROUTER_PATHS.claimDetailGenerateDocumentStatus.url,
  claimDetailInfo: ROUTER_PATHS.claimDetailInfo.url,
  claimDetailInspections: ROUTER_PATHS.claimDetailInspections.url,
  claimDetailInspectionDetail: ROUTER_PATHS.claimDetailInspectionDetail.url,
  claimDetailMeetings: ROUTER_PATHS.claimDetailMeetings.url,
  claimDetailNewMeeting: ROUTER_PATHS.claimDetailNewMeeting.url,
  claimDetailMeeting: ROUTER_PATHS.claimDetailMeeting.url,
  claimDetailNotes: ROUTER_PATHS.claimDetailNotes.url,
  claimDetailPhotoReport: ROUTER_PATHS.claimDetailPhotoReport.url,
  claimDetailPhotoReportStatus: ROUTER_PATHS.claimDetailPhotoReportStatus.url,
  claimDetailMedia: ROUTER_PATHS.claimDetailMedia.url,
  claimDetailUploadDocument: ROUTER_PATHS.claimDetailUploadDocument.url,

  jobDetail: ROUTER_PATHS.jobDetail.url,
  jobDetailAppointments: ROUTER_PATHS.jobDetailAppointments.url,
  jobDetailBillDetail: ROUTER_PATHS.jobDetailBillDetail.url,
  jobDetailBilling: ROUTER_PATHS.jobDetailBilling.url,
  jobDetailDocuments: ROUTER_PATHS.jobDetailDocuments.url,
  jobDetailInfo: ROUTER_PATHS.jobDetailInfo.url,
  jobDetailMedia: ROUTER_PATHS.jobDetailMedia.url,
  jobDetailInspections: ROUTER_PATHS.jobDetailInspections.url,
  jobDetailInspectionDetail: ROUTER_PATHS.jobDetailInspectionDetail.url,
  jobDetailMeetings: ROUTER_PATHS.jobDetailMeetings.url,
  jobDetailNewMeeting: ROUTER_PATHS.jobDetailNewMeeting.url,
  jobDetailMeeting: ROUTER_PATHS.jobDetailMeeting.url,
  jobDetailNotes: ROUTER_PATHS.jobDetailNotes.url,
  jobDetailPhotoReportStatus: ROUTER_PATHS.jobDetailPhotoReportStatus.url,
}
